import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Alert,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import {
  State,
  TeacherAdminDto,
  CreateTeacherAdminRequest,
} from "../../../interfaces";
import {
  addTeacherAdminUser,
  getTeacherAdminUser,
  updateTeacherAdminUser,
} from "../../../store/actions";
import { ActivityButton } from "../../../components/Common";
import { GenderEnum } from "../../../enums";
import { SubjectsSelect } from "../../../components";
import { InputType } from "reactstrap/types/lib/Input";
import { ToastContainer } from "react-toastify";
import moment from "moment";
import { SelectListDto } from "../../../models";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const AddEditAdminTeacherModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
}) => {
  //#region hooks
  const dispatch = useDispatch();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  //#endregion

  //#region internal states
  const [request] = useState<CreateTeacherAdminRequest>();
  const [passwordType, setPasswordType] = useState<InputType>("password");
  const [selectedSubjects, setSelectedSubjects] = useState<any[]>();
  //#endregion

  //#region redux state
  const {
    loading: addingTeacherAdminUser,
    success: addTeacherAdminUserSuccess,
    error: addTeacherAdminUserError,
  } = useSelector(
    (state: any): State<TeacherAdminDto> =>
      state.teacherAdminUser.addTeacherAdminUser
  );

  const {
    loading: updatingTeacherAdminUser,
    success: updateTeacherAdminUserSuccess,
    error: updateTeacherAdminUserError,
  } = useSelector(
    (state: any): State<TeacherAdminDto> =>
      state.teacherAdminUser.updateTeacherAdminUser
  );

  const {
    loading: gettingTeacherAdminUser,
    error: getTeacherAdminUserError,
    response: teacherAdmin,
  } = useSelector(
    (state: any): State<TeacherAdminDto> =>
      state.teacherAdminUser.teacherAdminUser
  );

  //#endregion

  //#region form
  const form = useFormik<CreateTeacherAdminRequest>({
    enableReinitialize: true,
    initialValues: {
      id: request?.id,
      name: {
        en: request?.name.en || "",
        ar: request?.name.ar || "",
      },
      countryCode:
        request?.countryCode || process.env.REACT_APP_COUNTRY_CODE || "965",
      email: request?.email || "",
      gender: request?.gender || GenderEnum.MALE,
      password: "",
      phoneNumber: request?.phoneNumber || "",
      deadline: request?.deadline || undefined,
      subjectId: request?.subjectId || [],
    },
    initialErrors: addTeacherAdminUserError || updateTeacherAdminUserError,
    validationSchema: Yup.object({
      id: Yup.string().optional(),
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" })
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            })
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" })
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            })
          ),
      }),
      phoneNumber: Yup.string().required(
        t("isNotEmpty", {
          displayName: "phoneNumber",
        })
      ),
      email: Yup.string().email(
        t("invalidEmail", {
          displayName: "email",
        })
      ),
      password: Yup.string()
        .min(8, t("minLength", { displayName: "password", minLength: 8 }))
        .max(25, t("maxLength", { displayName: "password", maxLength: 25 }))
        .when("id", (id: any): any => {
          if (id == "" || id == null || id == undefined)
            return Yup.string()
              .min(
                8,
                t("minLength", {
                  displayName: "password",
                  minLength: 8,
                })
              )
              .max(
                25,
                t("maxLength", {
                  displayName: "password",
                  maxLength: 25,
                })
              )
              .required(
                t("isNotEmpty", {
                  displayName: "password",
                })
              );
        }),
      deadline: Yup.string().required(
        t("isNotEmpty", {
          displayName: "deadline",
        })
      ),
      subjectId: Yup.array(),
    }),
    onSubmit: (values) => {
      if (
        !selectedSubjects ||
        !selectedSubjects.length ||
        selectedSubjects?.length === 0
      ) {
        toast.error(t("chooseAtLeast1Subject"), {
          autoClose: 3000,
        });
        return;
      }
      if (id) {
        dispatch(
          updateTeacherAdminUser(
            id,
            { ...values, subjectId: selectedSubjects?.map((s) => s.value) },
            history
          )
        );
      } else {
        dispatch(
          addTeacherAdminUser(
            { ...values, subjectId: selectedSubjects?.map((s) => s.value) },
            history
          )
        );
      }
    },
  });
  //#endregion

  //#region effects
  useEffect(() => {
    if (
      (!addingTeacherAdminUser && addTeacherAdminUserSuccess) ||
      (!updatingTeacherAdminUser && updateTeacherAdminUserSuccess)
    ) {
      onClose();
    }
  }, [addingTeacherAdminUser, updatingTeacherAdminUser]);

  useEffect(() => {
    if (id && teacherAdmin) {
      form.setValues({
        id: id,
        name: teacherAdmin.name,
        countryCode: teacherAdmin.countryCode,
        phoneNumber: teacherAdmin.phone,
        email: teacherAdmin.email || "",
        gender: teacherAdmin.gender,
        subjectId: teacherAdmin.subjects.map((s) => s.id) || [],
        deadline: teacherAdmin.deadline,
      });
      setSelectedSubjects(
        teacherAdmin.subjects.map((item: SelectListDto) => {
          return {
            value: item.id,
            label: i18n.language === "ar" ? item.value.ar : item.value.en,
          };
        })
      );
    }
  }, [teacherAdmin]);

  useEffect(() => {
    if (id) {
      dispatch(getTeacherAdminUser(id, history));
    }
  }, [id]);

  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
    setSelectedSubjects([]);
  };

  /**
   * Password show hide
   */
  const handleClickShowPassword = () => {
    if (passwordType === "text") setPasswordType("password");
    else setPasswordType("text");
  };

  const onChangeSubject = (changedValue: any) => {
    setSelectedSubjects(changedValue);
  };

  //#endregion

  return (
    <React.Fragment>
      <ToastContainer />
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="p-3"
            toggle={() => {
              onClose();
            }}
          >
            {id
              ? t("editItem", { item: "user" })
              : t("addItem", { item: "user" })}
          </ModalHeader>
          {typeof getTeacherAdminUserError === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{getTeacherAdminUserError}</p>
            </Alert>
          )}
          {typeof addTeacherAdminUserError === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{addTeacherAdminUserError}</p>
            </Alert>
          )}
          {typeof updateTeacherAdminUserError === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{updateTeacherAdminUserError}</p>
            </Alert>
          )}
          <ModalBody>
            <div className="mb-3">
              <label htmlFor="name.en" className="form-label">
                {t("nameEn")}
                <span className="text-danger">*</span>
              </label>
              <Input
                disabled={getTeacherAdminUserError}
                autoComplete="off"
                id="name.en"
                name="name.en"
                value={form.values?.name.en}
                type="text"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.en && form.errors.name?.en ? true : false
                }
              />
              {form.touched.name?.en && form.errors.name?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.en as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="name.ar" className="form-label">
                {t("nameAr")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ fontFamily: "aljannat", direction: "rtl" }}
                disabled={getTeacherAdminUserError}
                autoComplete="off"
                id="name.ar"
                name="name.ar"
                value={form.values?.name.ar}
                type="text"
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.ar && form.errors.name?.ar ? true : false
                }
              />
              {form.touched.name?.ar && form.errors.name?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <Label htmlFor="phoneNumber" className="form-label">
                {t("phoneNumber")}
              </Label>
              <span className="text-danger">*</span>
              <Input
                autoComplete="off"
                id="phoneNumber"
                name="phoneNumber"
                className="form-control"
                placeholder={t("enterPhoneNumber")}
                type="text"
                onBlur={form.handleBlur}
                value={form.values.phoneNumber || ""}
                invalid={
                  form.touched.phoneNumber && form.errors.phoneNumber
                    ? true
                    : false
                }
                maxLength={12}
                onChange={(e) => {
                  e.preventDefault();
                  const { value } = e.target;
                  const regex =
                    /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                  if (regex.test(value.toString())) {
                    form.setValues({
                      ...form.values,
                      phoneNumber: value,
                    });
                  } else {
                    form.setValues({
                      ...form.values,
                      phoneNumber: "",
                    });
                  }
                }}
              />
              {form.touched.phoneNumber && form.errors.phoneNumber ? (
                <FormFeedback type="invalid">
                  {form.errors.phoneNumber as string}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label className="form-label" htmlFor="password-input">
                {t("password")}
              </Label>
              {!id && <span className="text-danger">*</span>}
              <div className="position-relative auth-pass-inputgroup mb-3">
                <Input
                  id="password-input"
                  name="password"
                  value={form.values.password || ""}
                  type={passwordType}
                  className="form-control"
                  placeholder={t("enterPassword")}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  invalid={
                    form.touched.password && form.errors.password ? true : false
                  }
                  maxLength={25}
                />
                {form.touched.password && form.errors.password ? (
                  <FormFeedback type="invalid">
                    {form.errors.password as string}
                  </FormFeedback>
                ) : null}
                <button
                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                  type="button"
                  id="password-addon"
                >
                  <i
                    className="ri-eye-fill align-middle"
                    onClick={handleClickShowPassword}
                  ></i>
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="deadline" className="form-label">
                {t("deadline")}
                <span className="text-danger">*</span>
              </label>
              <Input
                disabled={getTeacherAdminUserError}
                autoComplete="off"
                id="deadline"
                name="deadline"
                value={
                  form.values.deadline
                    ? moment(form.values.deadline).format("yyyy-MM-DD")
                    : ""
                }
                type="date"
                className="form-control"
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.deadline && form.errors.deadline ? true : false
                }
              />
              {form.touched.deadline && form.errors.deadline ? (
                <FormFeedback type="invalid">
                  {form.errors.deadline as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="email" className="form-label">
                {t("email")}
              </label>
              <Input
                disabled={getTeacherAdminUserError}
                autoComplete="off"
                id="email"
                name="email"
                value={form.values?.email}
                type="text"
                className="form-control"
                placeholder={t("enterEmail")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={form.touched.email && form.errors.email ? true : false}
              />
              {form.touched.email && form.errors.email ? (
                <FormFeedback type="invalid">
                  {form.errors.email as string}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <label className="form-label">{t("gender")}</label>
              <div>
                <div className="form-check form-check-inline">
                  <Input
                    className="form-check-input"
                    type="radio"
                    name="inlineRadioOptions"
                    id="inlineRadio1"
                    value={GenderEnum.MALE}
                    checked={form.values.gender === GenderEnum.MALE}
                    onChange={() => {
                      form.setFieldValue("gender", GenderEnum.MALE);
                    }}
                  />
                  <label className="form-check-label" htmlFor="inlineRadio1">
                    {t("male")}
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <Input
                    className="form-check-input"
                    type="radio"
                    name="inlineRadioOptions"
                    id="inlineRadio2"
                    value={GenderEnum.FEMALE}
                    checked={form.values.gender === GenderEnum.FEMALE}
                    onChange={() => {
                      form.setFieldValue("gender", GenderEnum.FEMALE);
                    }}
                  />
                  <label className="form-check-label" htmlFor="inlineRadio2">
                    {t("female")}
                  </label>
                </div>
              </div>
            </div>
            <div className="mb-3">
              <Label htmlFor="subjectId" className="form-label">
                {t("subjects")}
                <span className="text-danger">*</span>
                <span className="text-info">
                  ({t("chooseAtLeast1Subject")})
                </span>
              </Label>
              <SubjectsSelect
                placeholder="filterBySubject"
                isMulti={true}
                closeMenuOnSelect={false}
                value={selectedSubjects}
                onChange={onChangeSubject}
              ></SubjectsSelect>
            </div>
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={addingTeacherAdminUser || updatingTeacherAdminUser}
              disabled={
                addingTeacherAdminUser ||
                updatingTeacherAdminUser ||
                getTeacherAdminUserError ||
                gettingTeacherAdminUser
              }
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
